import {
  Alert,
  Box,
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Company, setSelectedList } from '../../admin/admin-page/admin.slice';
import {
  deleteVesselLists,
  getVesselLists,
  updateListColour,
} from '../../api/admin/vessel-lists';
import { useAppDispatch, useAppSelector, useMobile } from '../../hooks';
import useAccessControl from '../../hooks/access-control/useAccessControl';
import {
  setVesselLists,
  setVesselListsError,
} from '../../state/vessels/vessels.slice';
import { kebabify } from '../../utils/text-formatting.utils';
import { getFallbackColor } from '../../utils/vessel-colour.utils';
import ColourPicker from '../colour-picker/colour-picker';
import DeleteModal from '../delete-modal/delete-modal';
import SimpleDialog from '../dialog/SimpleDialog';
import ErrorPanel from '../error-components/error-panel/error-panel';
import LoadingPanel from '../loading-panel/loading-panel';
import SimpleSearchBar from '../simple-search-bar/simple-search-bar';
import AddVesselListModalContents from './add-list-modal/add-list-modal';
import './manage-vessel-lists.scss';
import { VesselList } from './vessel-lists.model';

interface ManageVesselListsProps {
  selectedCompany: Company;
}

export const fetchVesselLists = (
  dispatch: ReturnType<typeof useAppDispatch>,
  companyId: string | undefined
) => {
  if (!companyId) return;

  dispatch(setVesselLists([]));
  const fetchPromise = getVesselLists(companyId);

  fetchPromise
    .then((response) => {
      const sortedResponse = response.sort((a, b) =>
        a.list_name.localeCompare(b.list_name)
      );
      dispatch(setVesselLists(sortedResponse));
    })
    .catch(() => {
      dispatch(setVesselListsError());
    });
};

function ManageVesselLists(props: ManageVesselListsProps) {
  const { selectedCompany } = props;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    vesselLists: lists,
    vesselListsError: listsError,
    vesselListsLoading: listsLoading,
  } = useAppSelector((state) => state.myFleet);

  const { canAccessVesselListColours } = useAccessControl();

  const [filterValue, setFilterValue] = useState<string>('');
  const [selectedLists, setSelectedLists] = useState<VesselList[]>([]);
  const [modalOpen, setModalOpen] = useState<'delete' | 'new' | null>(null);

  const [modalLoading, setModalLoading] = useState<boolean>(false);
  const [modalSuccess, setModalSuccess] = useState<boolean>(false);
  const [modalError, setModalError] = useState<boolean>(false);

  const isMobile = useMobile();

  const filteredLists = lists?.filter((list: VesselList) =>
    list.list_name.toLowerCase().includes(filterValue.toLowerCase())
  );

  const modifyselectedLists = (e: React.MouseEvent, list: VesselList) => {
    e.stopPropagation();
    if (
      selectedLists.some(
        (selectedList) => selectedList.list_id === list.list_id
      )
    ) {
      setSelectedLists((prevselectedLists: VesselList[]) =>
        prevselectedLists.filter(
          (prevSelectedList) => prevSelectedList.list_id !== list.list_id
        )
      );
    } else {
      setSelectedLists((oldArray) => [...oldArray, list]);
    }
  };

  const toggleAllListsSelected = () => {
    if (lists && selectedLists.length < lists.length) {
      lists.forEach((list) => {
        // if user isn't already a part of selectedIds then add it
        if (
          !selectedLists.some(
            (selectedList) => selectedList.list_id === list.list_id
          )
        ) {
          setSelectedLists((oldArray) => [...oldArray, list]);
        }
      });
    } else {
      setSelectedLists([]);
    }
  };

  const toggleModal = (modalToOpen: 'delete' | 'new') => {
    if (modalOpen) {
      setModalOpen(null);
      setModalError(false);
      setModalLoading(false);
      setModalSuccess(false);
    } else {
      setModalOpen(modalToOpen);
    }
  };

  const goToVesselListView = (list: VesselList) => {
    dispatch(setSelectedList(list));
    if (selectedCompany) {
      navigate(
        `/admin/companies/${kebabify(
          selectedCompany.company_name
        )}/vessel-lists/${kebabify(list.list_name.toLocaleLowerCase())}`
      );
    } else {
      navigate(
        `/user-preferences/vessel-lists/${kebabify(
          list.list_name.toLocaleLowerCase()
        )}`
      );
    }
  };

  const onDeleteVesselLists = async () => {
    setModalLoading(true);
    return deleteVesselLists(
      selectedLists.map((selectedList) => selectedList.list_id)
    )
      .then(() => {
        setModalLoading(false);
        setModalSuccess(true);
        setSelectedLists([]);
        return fetchVesselLists(dispatch, selectedCompany.company_id);
      })
      .catch(() => {
        setModalLoading(false);
        setModalError(true);
      });
  };

  const fallBackColour: string = getFallbackColor();

  const vesselListColour = (list: VesselList) =>
    list.list_colour || fallBackColour;

  const handleFleetColourChange = (colour: string, list_id: string) => {
    const newColour = colour === fallBackColour ? '' : colour;
    updateListColour(list_id, newColour)
      .then(() => getVesselLists(selectedCompany.company_id))
      .then((response) => {
        dispatch(
          setVesselLists(
            response.sort((a, b) => a.list_name.localeCompare(b.list_name))
          )
        );
      });
  };

  const handleFetchVesselLists = () => {
    fetchVesselLists(dispatch, selectedCompany.company_id);
  };

  useEffect(() => {
    if (!listsLoading && !listsError) {
      handleFetchVesselLists();
    }
  }, []);

  return (
    <Box sx={{ backgroundColor: 'background.menu' }}>
      <Typography variant="h6" sx={{ fontWeight: 400 }}>
        {lists ? `Vessel Lists: (${lists.length})` : 'Vessel Lists'}
      </Typography>
      {listsLoading && <LoadingPanel />}
      {listsError && !listsLoading && (
        <ErrorPanel message="Error loading Vessel Lists..." />
      )}
      {!listsLoading && !listsError && lists && (
        <Box sx={{ backgroundColor: 'background.menu' }}>
          <TableContainer
            sx={{
              height: isMobile ? '40vh' : '50vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              mt: '1rem',
              backgroundColor: 'background.menu',
            }}
          >
            <Table
              stickyHeader
              sx={{
                minWidth: isMobile ? undefined : 650,
                border: 'none',
                backgroundColor: 'background.menu',
              }}
              aria-label="simple table"
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      backgroundColor: 'background.menu',
                      borderBottomColor: 'background.menuBorder',
                    }}
                  >
                    <Box
                      sx={{
                        width: '70%',
                        backgroundColor: 'background.menu',
                      }}
                    >
                      <SimpleSearchBar
                        filterValue={filterValue}
                        handleChange={(value) => setFilterValue(value)}
                        placeholder="Search Vessel Lists"
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      textAlign: 'right',
                      backgroundColor: 'background.menu',
                      borderColor: 'background.menuBorder',
                    }}
                  >
                    <Checkbox
                      color="success"
                      sx={{
                        ml: '-.75rem',
                        backgroundColor: 'background.menu',
                      }}
                      checked={selectedLists.length === lists.length}
                      onClick={() => {
                        toggleAllListsSelected();
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ backgroundColor: 'Background.menu' }}>
                {filteredLists?.map((list: VesselList) => (
                  <TableRow
                    hover
                    key={list.list_id}
                    sx={{
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: 'Background.menu',
                    }}
                    onClick={() => goToVesselListView(list)}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={
                        isMobile
                          ? {
                              maxWidth: '7rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              backgroundColor: 'Background.menu',
                            }
                          : {}
                      }
                    >
                      {list.list_name}
                    </TableCell>
                    <TableCell
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        backgroundColor: 'Background.menu',
                      }}
                    >
                      {canAccessVesselListColours && (
                        <Box
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                          sx={{ padding: '0 0.5rem' }}
                        >
                          <ColourPicker
                            type="hex"
                            colour={vesselListColour(list)}
                            onChange={(newColour) => {
                              handleFleetColourChange(newColour, list.list_id);
                            }}
                            defaultColour={fallBackColour}
                            iconDefaultColour={vesselListColour(list)}
                            borderStyle="none"
                            hoverColour="primary.hover"
                          />
                        </Box>
                      )}
                      <Checkbox
                        onClick={(e: React.MouseEvent) => {
                          modifyselectedLists(e, list);
                        }}
                        color="success"
                        checked={selectedLists.some(
                          (selectedList) =>
                            selectedList.list_id === list.list_id
                        )}
                        inputProps={{
                          'aria-labelledby': list.list_name,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'right',
              backgroundColor: 'background.menu',
            }}
          >
            <Button
              onClick={() => toggleModal('delete')}
              disabled={!selectedLists.length}
              variant="outlined"
              sx={{
                m: '1rem 1.5rem',
                width: '74px',
              }}
            >
              Delete
            </Button>
            <Button
              onClick={() => toggleModal('new')}
              variant="contained"
              sx={{
                m: '1rem 1.5rem',
                width: '110px',
              }}
            >
              New List
            </Button>
          </Box>
        </Box>
      )}

      <DeleteModal
        error={modalError}
        loading={modalLoading}
        success={modalSuccess}
        visible={modalOpen === 'delete'}
        onClose={() => toggleModal('delete')}
        onCancel={() => toggleModal('delete')}
        title="Delete Vessel Lists"
        onDelete={onDeleteVesselLists}
        content={
          <div>
            <Alert severity="warning">
              This action cannot be undone, any associated Notifications will be
              removed <br />
              <br />
              Are you sure you want to delete these vessel lists?:
            </Alert>

            {lists && (
              <List>
                {lists
                  .filter((list: VesselList) =>
                    selectedLists.some(
                      (selectedList: VesselList) =>
                        selectedList.list_id === list.list_id
                    )
                  )
                  .map((list: VesselList) => (
                    <ListItem key={`${list.list_id}`}>
                      <ListItemText primary={list.list_name} />
                    </ListItem>
                  ))}
              </List>
            )}
          </div>
        }
      />

      <SimpleDialog
        open={modalOpen === 'new'}
        onClose={() => toggleModal('new')}
        title="Create New Vessel List"
      >
        <AddVesselListModalContents
          companyId={selectedCompany?.company_id}
          onCancel={() => toggleModal('new')}
          onCreateSuccess={handleFetchVesselLists}
        />
      </SimpleDialog>
    </Box>
  );
}

export default ManageVesselLists;
